import { FC, useState, useEffect, useCallback } from "react";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { SwitchProps, useSwitch } from "@heroui/switch";
import clsx from "clsx";
import { useTheme } from "@heroui/use-theme";
import { useUser } from "@clerk/clerk-react";

import { SunFilledIcon, MoonFilledIcon } from "@/components/icons";

export interface ThemeSwitchProps {
  className?: string;
  classNames?: SwitchProps["classNames"];
  /** Clerk metadata key to store the theme under (default: "uiTheme") */
  clerkThemeKey?: string;
}

export const ThemeSwitch: FC<ThemeSwitchProps> = ({
  className,
  classNames,
  clerkThemeKey = "theme",
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const { user, isSignedIn } = useUser();

  const { theme, setTheme } = useTheme();

  // Save theme to Clerk (unprotected client-writable metadata)
  const saveThemeToClerk = useCallback(
    async (nextTheme: "light" | "dark") => {
      try {
        if (!isSignedIn || !user) return;
        // Merge with existing unsafeMetadata (unprotected_data)
        const current = (user.unsafeMetadata || {}) as Record<string, unknown>;
        await user.update({
          unsafeMetadata: {
            ...current,
            [clerkThemeKey]: nextTheme,
          },
        });
      } catch (err) {
        console.error("Failed to update Clerk unsafeMetadata theme:", err);
      }
    },
    [isSignedIn, user, clerkThemeKey]
  );

  const {
    Component,
    slots,
    isSelected,
    getBaseProps,
    getInputProps,
    getWrapperProps,
  } = useSwitch({
    isSelected: theme === "light",
    onChange: () => {
      const next = theme === "light" ? "dark" : "light";
      setTheme(next);
      void saveThemeToClerk(next);
    },
  });

  useEffect(() => {
    setIsMounted(true);
  }, []); // <- fix: mount once

  if (!isMounted) return <div className="w-6 h-6" />;

  return (
    <Component
      aria-label={isSelected ? "Switch to dark mode" : "Switch to light mode"}
      {...getBaseProps({
        className: clsx(
          "px-px transition-opacity hover:opacity-80 cursor-pointer",
          className,
          classNames?.base
        ),
      })}
    >
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
      <div
        {...getWrapperProps()}
        className={slots.wrapper({
          class: clsx(
            [
              "w-auto h-auto",
              "bg-transparent",
              "rounded-lg",
              "flex items-center justify-center",
              "group-data-[selected=true]:bg-transparent",
              "!text-default-500",
              "pt-px",
              "px-0",
              "mx-0",
            ],
            classNames?.wrapper
          ),
        })}
      >
        {isSelected ? <MoonFilledIcon size={22} /> : <SunFilledIcon size={22} />}
      </div>
    </Component>
  );
};